"use client";

// Le Veilleur (la Veille, avant) : le second agent de l'entreprise toujours là (api/app/veille.py), qui surveille les
// appels d'offres de la Marketplace. Son visage, un théodolite de géomètre sur son trépied, au bleu des plans : en veille, la lunette balaie
// lentement l'horizon ; quand elle vous répond, elle vise, et son faisceau part vers l'appel d'offres. Sa carte dit ce
// qu'elle voit : les appels d'offres ouverts, et ceux qui vont bien à l'entreprise.

import { useCallback, useEffect, useState } from "react";
import { api, EVENEMENTS, type Evenement, type Veille } from "@/lib/api";
import "./veille.css";

export function Theodolite({
  vise = false,
  className = "",
}: {
  vise?: boolean; // elle travaille : la lunette vise, le faisceau part
  className?: string;
}) {
  return (
    <svg
      className={`theodolite ${vise ? "vise" : ""} ${className}`}
      viewBox="0 0 36 32"
      aria-hidden
    >
      {/* le faisceau de visée, derrière la lunette */}
      <path className="theo-faisceau" d="M26 9.2 36 5v9l-10-3.4z" />
      {/* le trépied et son plateau */}
      <g className="theo-trepied">
        <path d="M18 17.5 9 31M18 17.5V31M18 17.5 27 31" />
      </g>
      <rect
        className="theo-plateau"
        x="12.5"
        y="15.4"
        width="11"
        height="2.4"
        rx="1"
      />
      {/* la tête : la fourche, la lunette, son objectif et le niveau */}
      <g className="theo-tete">
        <path className="theo-fourche" d="M14 15.4V8.5M22 15.4V8.5" />
        <rect
          className="theo-lunette"
          x="10.5"
          y="7.4"
          width="15"
          height="4.6"
          rx="1.6"
        />
        <circle className="theo-objectif" cx="25.6" cy="9.7" r="2.1" />
        <rect
          className="theo-niveau"
          x="15.2"
          y="4.6"
          width="5.6"
          height="1.8"
          rx="0.9"
        />
        <circle className="theo-bulle" cx="18" cy="5.5" r="0.55" />
      </g>
    </svg>
  );
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? "s" : ""}`;

export function ligneVeille(v: Veille, occupe: boolean): string {
  if (occupe) return "vous répond";
  if (!v.ouvertes) return "aucun appel d'offres ouvert";
  const fortes = v.fortes ? `, ${pluriel(v.fortes, "bien noté")}` : "";
  return `${pluriel(v.ouvertes, "AO ouvert")}${fortes}`;
}

// Ce qu'elle voit, en direct : relu quand un tour d'agent commence ou finit, et quand le sourcing ramène ou note des
// annonces.
export function useVeille() {
  const [v, setV] = useState<Veille | null>(null);

  const charger = useCallback(async () => {
    try {
      setV(await api.veille());
    } catch {
      // Une API d'avant la Veille : la page fait sans.
    }
  }, []);

  useEffect(() => {
    void charger();
    let attente: number | undefined;
    const flux = new EventSource(EVENEMENTS);
    flux.onmessage = (m) => {
      const e = JSON.parse(m.data) as Evenement;
      if (e.type === "agent" || e.type === "sourcing" || e.type === "affaire") {
        window.clearTimeout(attente);
        attente = window.setTimeout(() => void charger(), 300);
      }
    };
    return () => {
      flux.close();
      window.clearTimeout(attente);
    };
  }, [charger]);

  return v;
}

// Sa place en tête des agents, sous l'Orchestrateur : épinglée, sans croix pour la supprimer.
export function CarteAgentVeille({
  v,
  occupe,
}: {
  v: Veille;
  occupe: boolean;
}) {
  return (
    <span className="veille-carte" title={ligneVeille(v, occupe)}>
      <Theodolite vise={occupe} />
      <span className="veille-carte-texte">
        <span className="veille-nom">{v.nom}</span>
        <span className="veille-ligne">Cherche des offres</span>
      </span>
    </span>
  );
}

// L'en-tête du chat quand son fil est ouvert : ce qu'elle voit, et de quoi ouvrir la Marketplace.
export function EnteteVeille({
  v,
  occupe,
  onMarketplace,
}: {
  v: Veille;
  occupe: boolean;
  onMarketplace?: () => void;
}) {
  return (
    <span className="veille-entete">
      <Theodolite vise={occupe} />
      <strong>{v.nom}</strong>
      <span className="veille-ligne">{ligneVeille(v, occupe)}</span>
      {onMarketplace && (
        <span className="veille-commandes">
          <button
            type="button"
            className="bouton-petit discret"
            onClick={onMarketplace}
          >
            Ouvrir la Marketplace
          </button>
        </span>
      )}
    </span>
  );
}
